import React from 'react';
import { Card, Space, Typography, Alert, Table, Tag, Collapse } from 'antd';
import {
  LinkOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { FailureLogEntry, FailureError } from '../types';
import { MethodBadge } from '../Common';
import JsonViewer from '@src/components/Common/JsonViewer';
import moment from 'moment';

const { Text } = Typography;
const { Panel } = Collapse;

interface FailureLogViewerProps {
  failureLog: FailureLogEntry[];
}

export const FailureLogViewer: React.FC<FailureLogViewerProps> = ({ failureLog }) => {
  if (!failureLog || failureLog.length === 0) {
    return null;
  }

  // Sort by pipeline version descending (newest first)
  const sortedFailures = [...failureLog].sort(
    (a, b) => b.pipeline_version - a.pipeline_version
  );

  // Count total errors across all entries
  const totalErrors = failureLog.reduce((sum, entry) => sum + entry.errors.length, 0);

  // Error table columns
  const errorColumns = [
    {
      title: 'Step',
      dataIndex: 'step',
      key: 'step',
      ellipsis: true,
      render: (step: string) => <Text strong>{step}</Text>,
    },
    {
      title: 'Method',
      dataIndex: 'method',
      key: 'method',
      width: 100,
      render: (method: string) => <MethodBadge method={method as any} />,
    },
    {
      title: 'Endpoint URL',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
      render: (url: string) => (
        <Text code style={{ fontSize: '11px' }}>
          <LinkOutlined style={{ marginRight: '4px' }} />
          {url}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status_code',
      key: 'status_code',
      width: 100,
      render: (status: number) => <Tag color="orange">HTTP {status}</Tag>,
    },
  ];

  return (
    <Card
      title={
        <Space>
          <ClockCircleOutlined style={{ fontSize: '18px', color: '#8c8c8c' }} />
          <span style={{ fontSize: '16px', fontWeight: 600 }}>
            Failure History ({totalErrors} error{totalErrors !== 1 ? 's' : ''} across {failureLog.length} execution{failureLog.length !== 1 ? 's' : ''})
          </span>
        </Space>
      }
      style={{
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        border: '1px solid #d9d9d9',
        background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)'
      }}
      headStyle={{
        background: 'linear-gradient(135deg, #f5f5f5 0%, #fafafa 100%)',
        borderBottom: '1px solid #d9d9d9'
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Alert
          message="Failure Log"
          description="This log tracks execution failures across pipeline versions, showing what errors occurred and helping diagnose issues."
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
        />

        {/* Group by execution (each failure log entry) */}
        <Collapse defaultActiveKey={[]} ghost>
          {sortedFailures.map((entry, entryIndex) => (
            <Panel
              key={`${entry.pipeline_version}-${entryIndex}`}
              header={
                <Space>
                  <Tag color="default">Version {entry.pipeline_version}</Tag>
                  <Text type="secondary">
                    {entry.failed_steps} of {entry.total_steps} step{entry.total_steps !== 1 ? 's' : ''} failed
                  </Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {moment(entry.timestamp).format('YYYY-MM-DD HH:mm:ss')}
                  </Text>
                </Space>
              }
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {/* Execution message */}
                <Alert
                  message={entry.message}
                  type="warning"
                  style={{ marginBottom: '8px' }}
                />

                {/* Errors table */}
                <Table
                  columns={errorColumns}
                  dataSource={entry.errors}
                  rowKey={(record, index) => `${entry.pipeline_version}-error-${index}`}
                  pagination={false}
                  size="small"
                  expandable={{
                    expandedRowRender: (error: FailureError) => (
                      <div
                        style={{
                          padding: '16px',
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          borderRadius: '8px',
                          border: '1px solid #e8e8e8'
                        }}
                      >
                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                          {/* Error message */}
                          <div>
                            <Text strong style={{ fontSize: '13px', color: '#595959' }}>
                              Error:
                            </Text>
                            <Alert
                              message={error.error}
                              type="warning"
                              style={{
                                marginTop: '8px',
                                fontSize: '12px',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                              }}
                            />
                          </div>

                          {/* Details if present */}
                          {error.details && error.details.length > 0 && (
                            <div>
                              <Text strong style={{ fontSize: '13px', color: '#595959' }}>
                                Details:
                              </Text>
                              <div style={{ marginTop: '8px' }}>
                                {error.details.map((detail, idx) => (
                                  <div
                                    key={idx}
                                    style={{
                                      padding: '8px 12px',
                                      background: '#f5f5f5',
                                      borderRadius: '4px',
                                      marginBottom: '4px',
                                      fontSize: '12px'
                                    }}
                                  >
                                    <Text code style={{ marginRight: '8px' }}>{detail.code}</Text>
                                    <Text>{detail.details}</Text>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Response body if present */}
                          {error.response_body && (
                            <div>
                              <Text strong style={{ fontSize: '13px', color: '#1890ff' }}>
                                Response Body:
                              </Text>
                              <div
                                style={{
                                  marginTop: '8px',
                                  maxHeight: '200px',
                                  overflow: 'auto',
                                  borderRadius: '6px'
                                }}
                              >
                                <JsonViewer
                                  data={error.response_body}
                                  fontSize="12px"
                                  lineHeight="1.5"
                                />
                              </div>
                            </div>
                          )}
                        </Space>
                      </div>
                    ),
                    rowExpandable: () => true,
                  }}
                />
              </Space>
            </Panel>
          ))}
        </Collapse>
      </Space>
    </Card>
  );
};

export default FailureLogViewer;
